import React, {useEffect, useState} from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "./TelemedicineRoom.css";
import {DataGrid, GridToolbar} from "@mui/x-data-grid";
import {Autocomplete, TextField} from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {baseUrl} from "../Univ.js";
// ============================================================
// COMPONENT
// ============================================================
function TelemedicineAppointments() {
    const [doctors, setDoctors] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [appointmentDate, setAppointmentDate] = useState(null);
    const [loadingDoctors, setLoadingDoctors] = useState(false);
    const [loadingAppointments, setLoadingAppointments] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    // ========================================================
    // PATIENT
    // ========================================================
    const patientId =
        localStorage.getItem("patient_id") ||
        localStorage.getItem("patientId");
    // ========================================================
    // INITIAL LOAD
    // ========================================================
    useEffect(() => {
        loadDoctors();
        loadAppointments();
    }, []);
    // ========================================================
    // LOAD DOCTORS
    // ========================================================
    const loadDoctors = async () => {
        try {
            setLoadingDoctors(true);
            setError("");
            const response = await axios.get(
                `${baseUrl}telemedicine/doctors/`
            );
            if (response.data?.success) {
                setDoctors(
                    response.data.doctors || []
                );
            } else {
                setDoctors([]);
                setError(
                    response.data?.message ||
                    "Unable to load doctors."
                );
            }
        } catch (error) {
            console.error(
                "TELEMEDICINE DOCTORS:",
                error
            );
            setDoctors([]);
            setError(
                error?.response?.data?.message ||
                "Unable to load telemedicine doctors."
            );
        } finally {
            setLoadingDoctors(false);
        }
    };
    // ========================================================
    // LOAD APPOINTMENTS
    // ========================================================
    const loadAppointments = async () => {
        try {
            setLoadingAppointments(true);
            const response = await axios.get(
                `${baseUrl}telemedicine/appointments/my/`
            );
            if (response.data?.success) {
                setAppointments(
                    response.data.appointments || []
                );
            } else {
                setAppointments([]);
            }
        } catch (error) {
            console.error(
                "TELEMEDICINE APPOINTMENTS:",
                error
            );
            setAppointments([]);
            setError(
                error?.response?.data?.message ||
                "Unable to load telemedicine appointments."
            );
        } finally {
            setLoadingAppointments(false);
        }
    };
    // ========================================================
    // BOOK APPOINTMENT
    // ========================================================
    const bookAppointment = async () => {
        setError("");
        // ----------------------------------------------------
        // PATIENT
        // ----------------------------------------------------
        if (!patientId) {
            Swal.fire({
                icon: "error",
                title: "Patient Session Missing",
                text: "Please login again.",
            });
            return;
        }
        // ----------------------------------------------------
        // DOCTOR
        // ----------------------------------------------------
        if (!selectedDoctor) {
            Swal.fire({
                icon: "warning",
                title: "Doctor Required",
                text: "Please select a doctor.",
            });
            return;
        }
        // ----------------------------------------------------
        // DATE
        // ----------------------------------------------------
        if (!appointmentDate) {
            Swal.fire({
                icon: "warning",
                title: "Appointment Time Required",
                text: "Please select the appointment date and time.",
            });
            return;
        }
        try {
            setSaving(true);
            // =================================================
            // SEND
            // =================================================
            const response = await axios.post(
                `${baseUrl}telemedicine/appointments/book/`,
                {
                    doctor: selectedDoctor.id,
                    appointment_date:
                        appointmentDate.toISOString()
                }
            );
            // =================================================
            // SUCCESS
            // =================================================
            if (response.data?.success) {
                await Swal.fire({
                    icon: "success",
                    title: "Appointment Booked",
                    text:
                        "Your telemedicine appointment has been booked successfully.",
                    confirmButtonText: "OK",
                    confirmButtonColor: "#198754"
                });
                // ------------------------------------------------
                // RESET
                // ------------------------------------------------
                setSelectedDoctor(null);
                setAppointmentDate(null);
                // ------------------------------------------------
                // RELOAD APPOINTMENTS
                // ------------------------------------------------
                await loadAppointments();
            }
        } catch (error) {
            console.error(
                "TELEMEDICINE BOOKING:",
                error
            );
            const message =
                error?.response?.data?.message ||
                "Unable to book telemedicine appointment.";
            Swal.fire({
                icon: "error",
                title: "Booking Failed",
                text: message,
                confirmButtonText: "OK"
            });
        } finally {
            setSaving(false);
        }
    };
    // ========================================================
    // DATE FORMAT
    // ========================================================
    const formatDateTime = (value) => {
        if (!value) {
            return "";
        }
        return new Date(value).toLocaleString(
            "en-IN",
            {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit"
            }
        );
    };
    // ========================================================
    // DOCTOR LABEL
    // ========================================================
    const doctorLabel = (doctor) => {
        if (!doctor) {
            return "";
        }
        let label =
            doctor.doctor_name || doctor.consultant || "";
        if (doctor.department) {
            label +=
                ` — ${doctor.department}`;
        }
        if (doctor.specialization) {
            label +=
                ` — ${doctor.specialization}`;
        }
        return label;
    };
    // ========================================================
    // DATAGRID
    // ========================================================
    const columns = [
        {
            field: "id",
            headerName: "Appointment",
            width: 120
        },
        {
            field: "doctor_name",
            headerName: "Doctor",
            width: 220
        },
        {
            field: "doctor_department",
            headerName: "Department",
            width: 180
        },
        {
            field: "doctor_specialization",
            headerName: "Specialization",
            width: 220
        },
        {
            field: "appointment_date",
            headerName:
                "Appointment Date & Time",
            width: 220,
            renderCell: (params) => (
                <span>
                    {formatDateTime(
                        params.value
                    )}
                </span>
            )
        },
        {
            field: "status",
            headerName: "Status",
            width: 140,
            renderCell: (params) => {
                const status =
                    String(
                        params.value || ""
                    ).toLowerCase();
                let badge =
                    "bg-primary";
                if (
                    status === "completed"
                ) {
                    badge =
                        "bg-success";
                } else if (
                    status === "cancelled" ||
                    status === "canceled"
                ) {
                    badge =
                        "bg-danger";
                } else if (
                    status === "scheduled"
                ) {
                    badge =
                        "bg-primary";
                }
                return (
                    <span
                        className={
                            `badge ${badge}`
                        }
                    >

                        {params.value}

                    </span>
                );
            }
        },
        // ====================================================
        // JOIN
        // ====================================================
        {
            field: "action",
            headerName: "Action",
            width: 200,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const status =
                    String(
                        params.row.status || ""
                    ).toLowerCase();
                if (
                    status !== "scheduled"
                ) {
                    return (
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            disabled
                        >

                            Not Available

                        </button>
                    );
                }
                return (
                    <button

                        type="button"

                        className="btn btn-success btn-sm"

                        onClick={() => {
                            localStorage.setItem(
                                "telemedicine_role",
                                "patient"
                            );
                            window.location.href =
                                `/telemedicine-room/${params.row.id}`;
                        }}

                    >

                        <i className="fa-solid fa-video me-2"></i>

                        Join Telemedicine

                    </button>
                );
            }
        }
    ];
    // ========================================================
    // UI
    // ========================================================
    return (
        <section className="container-fluid mt-4">


            {/* =================================================
                BOOK APPOINTMENT
            ================================================= */}

            <div className="card shadow-sm">

                <div className="card-header">

                    <i className="fa-solid fa-video me-2"></i>

                    Book Telemedicine Appointment

                </div>


                <div className="card-body">

                    {error && (
                        <div className="alert alert-danger">

                            {error}

                        </div>
                    )}


                    <div className="row g-4">


                        {/* =====================================
                            DOCTOR
                        ===================================== */}

                        <div className="col-lg-5">

                            <label className="form-label">

                                Select Doctor

                            </label>


                            <Autocomplete

                                options={doctors}

                                value={
                                    selectedDoctor
                                }

                                onChange={(
                                    event,
                                    value
                                ) => {
                                    setSelectedDoctor(
                                        value
                                    );
                                }}

                                loading={
                                    loadingDoctors
                                }

                                getOptionLabel={
                                    doctorLabel
                                }

                                isOptionEqualToValue={(
                                    option,
                                    value
                                ) =>
                                    option.id ===
                                    value.id
                                }

                                noOptionsText={
                                    loadingDoctors
                                        ? "Loading doctors..."
                                        : "No doctors found"
                                }

                                renderOption={(
                                    props,
                                    doctor
                                ) => (
                                    <li
                                        {...props}
                                        key={
                                            doctor.id
                                        }
                                    >

                                        <div>

                                            <div
                                                style={{
                                                    fontWeight:
                                                        600
                                                }}
                                            >

                                                {doctor.doctor_name}

                                            </div>


                                            <div
                                                style={{
                                                    fontSize:
                                                        "0.85rem",
                                                    color:
                                                        "#6c757d"
                                                }}
                                            >

                                                {doctor.department}

                                                {doctor.specialization
                                                    ? ` • ${doctor.specialization}`
                                                    : ""}

                                            </div>

                                        </div>

                                    </li>
                                )}

                                renderInput={(
                                    params
                                ) => (
                                    <TextField
                                        {...params}

                                        placeholder={
                                            loadingDoctors
                                                ? "Loading doctors..."
                                                : "Search doctor..."
                                        }

                                        size="small"

                                    />
                                )}

                            />

                        </div>


                        {/* =====================================
                            DATE
                        ===================================== */}

                        <div className="col-lg-4">

                            <label className="form-label">

                                Appointment Date & Time

                            </label>


                            <div
                                className="telemedicine-datepicker"
                            >

                                <DatePicker

                                    selected={
                                        appointmentDate
                                    }

                                    onChange={
                                        setAppointmentDate
                                    }

                                    showTimeSelect

                                    timeIntervals={
                                        15
                                    }

                                    dateFormat={
                                        "dd/MM/yyyy h:mm aa"
                                    }

                                    minDate={
                                        new Date()
                                    }

                                    placeholderText={
                                        "Select date and time"
                                    }

                                    className={
                                        "form-control"
                                    }

                                    popperPlacement={
                                        "bottom-start"
                                    }

                                    popperClassName={
                                        "telemedicine-datepicker-popper"
                                    }

                                />

                            </div>

                        </div>


                        {/* =====================================
                            BOOK
                        ===================================== */}

                        <div className="col-lg-3 d-flex align-items-end">

                            <button

                                type="button"

                                className="btn btn-success w-100"

                                onClick={
                                    bookAppointment
                                }

                                disabled={
                                    saving
                                }

                                style={{
                                    height: "40px"
                                }}

                            >

                                {saving ? (
                                    <>

                                        <span
                                            className={
                                                "spinner-border " +
                                                "spinner-border-sm " +
                                                "me-2"
                                            }
                                        />

                                        Booking...

                                    </>
                                ) : (
                                    <>

                                        <i
                                            className={
                                                "fa-solid " +
                                                "fa-calendar-check " +
                                                "me-2"
                                            }
                                        />

                                        Book Appointment

                                    </>
                                )}

                            </button>

                        </div>

                    </div>

                </div>

            </div>


            {/* =================================================
                APPOINTMENTS
            ================================================= */}

            <div className="card shadow-sm mt-4">

                <div className="card-header">

                    <i className="fa-solid fa-calendar-days me-2"></i>

                    My Telemedicine Appointments

                </div>


                <div className="card-body">

                    <div
                        className="telemedicine-grid-wrapper"
                    >

                        <DataGrid

                            rows={
                                appointments
                            }

                            columns={
                                columns
                            }

                            loading={
                                loadingAppointments
                            }

                            autoHeight

                            disableRowSelectionOnClick

                            slots={{
                                toolbar:
                                GridToolbar
                            }}

                            pageSizeOptions={[
                                10,
                                25,
                                50
                            ]}

                        />

                    </div>

                </div>

            </div>

        </section>
    );
}
export default TelemedicineAppointments;